<template>
  <div :class="['showcase-school-card', `showcase-school-card--${props.variant}`]">
    <div class="showcase-school-card__hero">
      <img
        :src="props.school.backgroundImage"
        :alt="props.school.name"
        class="showcase-school-card__hero-img"
        width="320"
        height="120"
        loading="lazy"
        decoding="async"
        @error="onHeroError" />
      <span
        v-if="props.school.featured"
        class="showcase-school-card__badge">
        ⭐ À la une
      </span>
      <button
        type="button"
        class="showcase-school-card__fav"
        aria-label="Favori">
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          aria-hidden="true">
          <path
            d="M8 13.5S2 9.5 2 5.5A3 3 0 0 1 8 4a3 3 0 0 1 6 1.5C14 9.5 8 13.5 8 13.5z"
            stroke="currentColor"
            stroke-width="1.5"
            fill="none" />
        </svg>
      </button>
      <div
        v-if="props.school.logoImage || props.school.score != null"
        class="showcase-school-card__hero-footer">
        <div
          v-if="props.school.logoImage"
          class="showcase-school-card__logo">
          <PrepeersEntityLogo
            :logo-pict="props.school.logoImage"
            :entity-name="props.school.subtitle"
            variant="card"
            :alt="`${props.school.subtitle} logo`" />
        </div>
        <div
          v-if="props.school.score != null"
          class="showcase-school-card__score-wrap">
          <svg
            viewBox="0 0 48 48"
            width="48"
            height="48"
            aria-hidden="true">
            <circle
              cx="24"
              cy="24"
              r="20"
              fill="rgba(0,0,0,0.4)"
              stroke="rgba(255,255,255,0.1)"
              stroke-width="3" />
            <circle
              cx="24"
              cy="24"
              r="20"
              fill="none"
              :stroke="`url(#${scoreGradientId})`"
              stroke-width="3"
              stroke-dasharray="125.7"
              :stroke-dashoffset="scoreStrokeOffset"
              stroke-linecap="round"
              transform="rotate(-90 24 24)" />
            <defs>
              <linearGradient
                :id="scoreGradientId"
                x1="0"
                y1="0"
                x2="0"
                y2="1">
                <stop
                  offset="0%"
                  stop-color="#10b981" />
                <stop
                  offset="100%"
                  stop-color="#06b6d4" />
              </linearGradient>
            </defs>
          </svg>
          <span class="showcase-school-card__score-val">{{ props.school.score }}</span>
        </div>
      </div>
    </div>
    <div class="showcase-school-card__body">
      <h3 class="showcase-school-card__title">{{ props.school.name }}</h3>
      <p class="showcase-school-card__subtitle">{{ props.school.subtitle }}</p>
      <div
        v-if="showLocations || showTypes"
        class="showcase-school-card__info">
        <p
          v-if="showLocations"
          class="showcase-school-card__info-row">
          <span
            class="showcase-school-card__info-icon"
            aria-hidden="true"
            >📍</span
          >
          {{ props.school.locations?.join(', ') }}
        </p>
        <p
          v-if="showTypes"
          class="showcase-school-card__info-row">
          <span
            class="showcase-school-card__info-icon"
            aria-hidden="true"
            >🎓</span
          >
          {{ props.school.types?.join(', ') }}
        </p>
      </div>
      <div
        v-if="showTags"
        class="showcase-school-card__tags">
        <span
          v-for="tag in props.school.tags ?? []"
          :key="tag"
          class="showcase-school-card__tag">
          {{ tag }}
        </span>
      </div>
      <div class="showcase-school-card__actions">
        <button
          type="button"
          class="showcase-school-card__btn showcase-school-card__btn--outline">
          Consulter
        </button>
        <button
          type="button"
          class="showcase-school-card__btn showcase-school-card__btn--orange">
          Contacter
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { ComputedRef, PropType } from 'vue'
import PrepeersEntityLogo from '~/components/core/PrepeersEntityLogo.vue'
import type { ShowcaseSchoolCardData } from '~/types/homeShowcases'
import type { ShowcaseSchoolCardProps, ShowcaseSchoolCardVariant } from '~/types/ShowcaseSchoolCard'
import { SHOWCASE_HERO_FALLBACK_BACKGROUND } from '~/utils/homeShowcases'

const SCORE_CIRCUMFERENCE = 125.7

/**
 * Définit les props du composant ShowcaseSchoolCard.
 */
const props: ShowcaseSchoolCardProps = defineProps({
  school: {
    type: Object as PropType<ShowcaseSchoolCardData>,
    required: true,
  },
  variant: {
    type: String as PropType<ShowcaseSchoolCardVariant>,
    required: true,
  },
})

const showLocations: ComputedRef<boolean> = computed(() => {
  return (props.school.locations?.length && props.school.locations[0]?.trim() !== '') || false
})

const showTypes: ComputedRef<boolean> = computed(() => {
  return (props.school.types?.length && props.school.types[0]?.trim() !== '') || false
})

const showTags: ComputedRef<boolean> = computed(() => {
  return (props.school.tags?.length && props.school.tags[0]?.trim() !== '') || false
})
/**
 * Applique le dégradé de secours sur le hero si l'image ne charge pas.
 * @param {Event} event - Événement `error` de l'image.
 */
function onHeroError(event: Event): void {
  const target: HTMLImageElement | null = event.target as HTMLImageElement | null
  const parent: HTMLElement | null = target?.parentElement ?? null
  if (!parent) return
  parent.style.background = SHOWCASE_HERO_FALLBACK_BACKGROUND
}

const scoreGradientId: ComputedRef<string> = computed(
  (): string => `showcase-school-score-${props.variant}`,
)

const scoreStrokeOffset: ComputedRef<number> = computed((): number => {
  const score: number = props.school.score ?? 0
  return SCORE_CIRCUMFERENCE * (1 - score / 100)
})
</script>

<style scoped>
.showcase-school-card {
  position: absolute;
  background: #1a1a2e;
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-lg);
  width: 280px;
}

.showcase-school-card--back {
  top: 0;
  left: 0;
  z-index: 1;
  transform: rotate(-2deg);
}

.showcase-school-card--front {
  top: 80px;
  right: 0;
  z-index: 2;
  transform: rotate(1deg);
}

.showcase-school-card__hero {
  position: relative;
  height: 120px;
  overflow: visible;
  background: #2a2a3e;
  border-radius: var(--radius-xl) var(--radius-xl) 0 0;
}

.showcase-school-card__hero-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: var(--radius-xl) var(--radius-xl) 0 0;
}

.showcase-school-card__badge {
  position: absolute;
  top: var(--space-2);
  left: var(--space-3);
  font-size: var(--text-xs);
  font-weight: 600;
  color: #fff;
  background: rgba(249, 115, 22, 0.9);
  border-radius: var(--radius-full);
  padding: 0.2em 0.7em;
}

.showcase-school-card__fav {
  position: absolute;
  top: var(--space-2);
  right: var(--space-3);
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.15);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  backdrop-filter: blur(4px);
}

.showcase-school-card__body {
  padding: var(--space-8) var(--space-4) var(--space-4);
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  border-radius: 0 0 var(--radius-xl) var(--radius-xl);
  position: relative;
  z-index: 1;
}

.showcase-school-card__hero-footer {
  position: absolute;
  bottom: -24px;
  left: var(--space-3);
  right: var(--space-3);
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  z-index: 10;
}

.showcase-school-card__score-wrap {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.showcase-school-card__score-val {
  position: absolute;
  font-size: var(--text-xs);
  font-weight: 800;
  color: #fff;
}

.showcase-school-card__logo {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: #c0392b;
  border: 3px solid #1a1a2e;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
}

.showcase-school-card__logo :deep(.entity-logo__img--card),
.showcase-school-card__logo :deep(.entity-logo__initials--card) {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.showcase-school-card__logo :deep(.entity-logo__initials--card) {
  background-color: transparent;
  font-size: 1.25rem;
}

.showcase-school-card__title {
  font-size: var(--text-base);
  font-weight: 700;
  color: var(--text-primary);
}

.showcase-school-card__subtitle {
  font-size: var(--text-xs);
  color: var(--text-secondary);
  margin-top: -4px;
}

.showcase-school-card__info {
  display: flex;
  flex-direction: column;
  gap: 3px;
  margin-top: var(--space-1);
}

.showcase-school-card__info-row {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  font-size: var(--text-xs);
  color: var(--text-muted);
}

.showcase-school-card__info-icon {
  flex-shrink: 0;
  font-size: 0.7rem;
}

.showcase-school-card__tags {
  display: flex;
  gap: var(--space-2);
  margin-top: var(--space-1);
}

.showcase-school-card__tag {
  font-size: var(--text-xs);
  font-weight: 600;
  color: var(--text-secondary);
  background: rgba(255, 255, 255, 0.08);
  border-radius: var(--radius-sm);
  padding: 0.2em 0.6em;
}

.showcase-school-card__actions {
  display: flex;
  gap: var(--space-2);
  margin-top: var(--space-2);
}

.showcase-school-card__btn {
  flex: 1;
  padding: 0.5em 0;
  border-radius: var(--radius-md);
  font-size: var(--text-xs);
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: filter var(--duration-fast) var(--ease-out);
}

.showcase-school-card__btn--outline {
  background: transparent;
  border: 1px solid var(--border-default);
  color: var(--text-secondary);
}

.showcase-school-card__btn--outline:hover {
  border-color: var(--border-strong);
  color: var(--text-primary);
}

.showcase-school-card__btn--orange {
  background: var(--accent-orange);
  color: #fff;
}

.showcase-school-card__btn--orange:hover {
  filter: brightness(1.1);
}

@media (max-width: 900px) {
  .showcase-school-card {
    width: 240px;
  }

  .showcase-school-card--back {
    left: 0;
  }

  .showcase-school-card--front {
    right: 0;
    top: 60px;
  }
}

@media (max-width: 480px) {
  .showcase-school-card {
    width: 200px;
  }

  .showcase-school-card__hero {
    height: 80px;
  }
}
</style>
